import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import './AuditLogs.css';

const API_URL = import.meta.env.VITE_API_URL;
const getApiUrl = (endpoint) => {
  const base = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
  return `${base}${endpoint}`;
};
const getToken = () => localStorage.getItem('token');

// ─── Helpers visuels ─────────────────────────────────────────────────────────

const ACTION_CONFIG = {
  CREATE_CLIENT:   { label: 'Création client',     color: '#10b981', icon: '➕', bg: '#d1fae5' },
  UPDATE_CLIENT:   { label: 'Modification client',  color: '#3b82f6', icon: '✏️', bg: '#dbeafe' },
  DELETE_CLIENT:   { label: 'Suppression client',   color: '#ef4444', icon: '🗑️', bg: '#fee2e2' },
  ADD_NOTE_CLIENT: { label: 'Note client',          color: '#8b5cf6', icon: '📝', bg: '#ede9fe' },
  CREATE_DOSSIER:  { label: 'Création dossier',     color: '#10b981', icon: '📁', bg: '#d1fae5' },
  UPDATE_DOSSIER:  { label: 'Modif. dossier',       color: '#3b82f6', icon: '✏️', bg: '#dbeafe' },
  DELETE_DOSSIER:  { label: 'Suppression dossier',  color: '#ef4444', icon: '🗑️', bg: '#fee2e2' },
  CHANGE_ETAT_DOSSIER: { label: 'Changement état', color: '#f59e0b', icon: '🔄', bg: '#fef3c7' },
  ADD_NOTE_DOSSIER: { label: 'Note dossier',        color: '#8b5cf6', icon: '📝', bg: '#ede9fe' },
  ADD_PAYMENT:     { label: 'Ajout paiement',       color: '#10b981', icon: '💰', bg: '#d1fae5' },
  UPDATE_PAYMENT:  { label: 'Modif. paiement',      color: '#3b82f6', icon: '💳', bg: '#dbeafe' },
  DELETE_PAYMENT:  { label: 'Suppression paiement', color: '#ef4444', icon: '💸', bg: '#fee2e2' },
  VALIDATE_PAYMENT:{ label: 'Validation paiement',  color: '#10b981', icon: '✅', bg: '#d1fae5' },
  CREATE_USER:     { label: 'Création utilisateur', color: '#10b981', icon: '👤', bg: '#d1fae5' },
  UPDATE_USER:     { label: 'Modif. utilisateur',   color: '#3b82f6', icon: '👤', bg: '#dbeafe' },
  DELETE_USER:     { label: 'Suppression utilisateur', color: '#ef4444', icon: '👤', bg: '#fee2e2' },
  LOGIN:           { label: 'Connexion',             color: '#10b981', icon: '🔐', bg: '#d1fae5' },
  LOGOUT:          { label: 'Déconnexion',           color: '#6b7280', icon: '🔓', bg: '#f3f4f6' },
  LOGIN_FAILED:    { label: 'Échec connexion',       color: '#ef4444', icon: '⚠️', bg: '#fee2e2' },
  PASSWORD_CHANGED:{ label: 'Mot de passe changé',  color: '#f59e0b', icon: '🔑', bg: '#fef3c7' },
  OTHER:           { label: 'Autre',                 color: '#6b7280', icon: '•',  bg: '#f3f4f6' },
};

const getActionConfig = (action) => ACTION_CONFIG[action] || ACTION_CONFIG.OTHER;

const ROLE_COLORS = {
  admin: '#1e3a5f', moderateur: '#667eea', commercial: '#10b981',
  installateur: '#f59e0b', client: '#38b2ac', entreprise: '#9f7aea',
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
};

// ─── Composant principal ──────────────────────────────────────────────────────
const AuditLogs = ({ onNavigate, onLogout, currentPage }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [expandedLog, setExpandedLog] = useState(null);
  const [selectedLogForModal, setSelectedLogForModal] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    search: '', action: '', userId: '', targetType: '',
    dateFrom: '', dateTo: '', role: ''
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/audit/stats'), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(res.data);
    } catch (err) { console.error('Stats audit:', err); }
  }, []);

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, ...filters });
      // Nettoyer les valeurs vides
      [...params.keys()].forEach(k => { if (!params.get(k)) params.delete(k); });
      const res = await axios.get(getApiUrl(`/audit/logs?${params}`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { page: 1, total: 0, pages: 1 });
    } catch (err) { console.error('Fetch logs:', err); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchSessions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/audit/sessions?page=${page}&limit=50`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessions(res.data.sessions || []);
      setPagination(res.data.pagination || { page: 1, total: 0, pages: 1 });
    } catch (err) { console.error('Fetch sessions:', err); }
    finally { setLoading(false); }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/audit/sessions/active'), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setActiveSessions(res.data.sessions || []);
    } catch (err) { console.error('Active sessions:', err); }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchActiveSessions();
    // Auto-refresh des sessions actives toutes les 30s
    const interval = setInterval(fetchActiveSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchActiveSessions]);

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs(1);
    else if (activeTab === 'sessions') fetchSessions(1);
  }, [activeTab, fetchLogs, fetchSessions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => fetchLogs(1);
  const handleResetFilters = () => {
    setFilters({ search: '', action: '', userId: '', targetType: '', dateFrom: '', dateTo: '', role: '' });
  };

  const handleExportCSV = async () => {
    const params = new URLSearchParams(filters);
    [...params.keys()].forEach(k => { if (!params.get(k)) params.delete(k); });
    const url = getApiUrl(`/audit/export/csv?${params}`);
    const link = document.createElement('a');
    link.href = `${url}&token=${getToken()}`;
    link.download = `audit_logs_${Date.now()}.csv`;
    link.click();
  };

  return (
    <div className="audit-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      <div className="audit-content">
        {/* ─── Header ────────────────────────────────────────────────────── */}
        <div className="audit-header">
          <div className="audit-header-left">
            <div className="audit-title-icon">🛡️</div>
            <div>
              <h1 className="audit-title">Audit Trail & Logs Système</h1>
              <p className="audit-subtitle">Traçabilité complète de toutes les actions du système</p>
            </div>
          </div>
          <div className="audit-header-actions">
            <button className="btn-export" onClick={handleExportCSV}>
              ⬇️ Exporter CSV
            </button>
          </div>
        </div>

        {/* ─── Stats Cards ───────────────────────────────────────────────── */}
        {stats && (
          <div className="audit-stats-grid">
            <div className="stat-card stat-blue">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats.totalLogs?.toLocaleString()}</div>
                <div className="stat-label">Total logs</div>
              </div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon">📅</div>
              <div className="stat-info">
                <div className="stat-value">{stats.logsLast30d?.toLocaleString()}</div>
                <div className="stat-label">30 derniers jours</div>
              </div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon">🟢</div>
              <div className="stat-info">
                <div className="stat-value">{activeSessions.length}</div>
                <div className="stat-label">Connectés maintenant</div>
              </div>
            </div>
            <div className="stat-card stat-purple">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <div className="stat-value">{stats.userBreakdown?.length || 0}</div>
                <div className="stat-label">Utilisateurs actifs (30j)</div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Sessions actives ──────────────────────────────────────────── */}
        {activeSessions.length > 0 && (
          <div className="active-sessions-bar">
            <span className="active-sessions-label">
              <span className="pulse-dot" />
              Connectés en ce moment :
            </span>
            {activeSessions.map(s => (
              <span key={s._id} className="active-session-chip">
                <span className="chip-role" style={{ background: ROLE_COLORS[s.role] || '#718096' }}>
                  {s.role}
                </span>
                {s.prenom} {s.nom}
                <span className="chip-ip">{s.ipAddress}</span>
              </span>
            ))}
          </div>
        )}

        {/* ─── Tabs ──────────────────────────────────────────────────────── */}
        <div className="audit-tabs">
          {[
            { id: 'logs', label: '📋 Journal d\'audit' },
            { id: 'sessions', label: '🔐 Sessions' },
            { id: 'stats', label: '📊 Statistiques' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`audit-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ─── Tab: Logs ─────────────────────────────────────────────────── */}
        {activeTab === 'logs' && (
          <div className="audit-logs-section">
            {/* Filtres */}
            <div className="filters-panel">
              <div className="filters-grid">
                <input
                  className="filter-input"
                  type="text"
                  name="search"
                  placeholder="🔍 Recherche globale..."
                  value={filters.search}
                  onChange={handleFilterChange}
                />
                <select className="filter-select" name="action" value={filters.action} onChange={handleFilterChange}>
                  <option value="">Toutes les actions</option>
                  {Object.entries(ACTION_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <select className="filter-select" name="targetType" value={filters.targetType} onChange={handleFilterChange}>
                  <option value="">Tous les types</option>
                  <option value="client">Client</option>
                  <option value="dossier">Dossier</option>
                  <option value="user">Utilisateur</option>
                  <option value="payment">Paiement</option>
                  <option value="session">Session</option>
                </select>
                <select className="filter-select" name="role" value={filters.role} onChange={handleFilterChange}>
                  <option value="">Tous les rôles</option>
                  <option value="admin">Admin</option>
                  <option value="moderateur">Modérateur</option>
                  <option value="commercial">Commercial</option>
                  <option value="installateur">Installateur</option>
                </select>
                <input
                  className="filter-input"
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  title="Date début"
                />
                <input
                  className="filter-input"
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  title="Date fin"
                />
              </div>
              <div className="filters-actions">
                <button className="btn-filter-apply" onClick={handleApplyFilters}>Appliquer</button>
                <button className="btn-filter-reset" onClick={handleResetFilters}>Réinitialiser</button>
              </div>
            </div>

            {/* Table */}
            <div className="logs-table-wrapper">
              <div className="logs-count">
                {pagination.total} entrée{pagination.total > 1 ? 's' : ''} trouvée{pagination.total > 1 ? 's' : ''}
              </div>
              {loading ? (
                <div className="audit-loading">
                  <div className="spinner" />
                  <span>Chargement des logs...</span>
                </div>
              ) : (
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Date / Heure</th>
                      <th>Action</th>
                      <th>Utilisateur</th>
                      <th>Rôle</th>
                      <th>Cible</th>
                      <th>Commentaire</th>
                      <th>IP</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const cfg = getActionConfig(log.action);
                      const isExpanded = expandedLog === log._id;
                      return (
                        <React.Fragment key={log._id}>
                          <tr className={`log-row ${isExpanded ? 'expanded' : ''}`}>
                            <td className="log-datetime">{formatDateTime(log.timestamp)}</td>
                            <td>
                              <span className="action-badge" style={{ background: cfg.bg, color: cfg.color }}>
                                {cfg.icon} {cfg.label}
                              </span>
                            </td>
                            <td className="log-actor">
                              {log.actor?.prenom} {log.actor?.nom}
                            </td>
                            <td>
                              <span className="role-pill" style={{ background: ROLE_COLORS[log.actor?.role] || '#718096' }}>
                                {log.actor?.role}
                              </span>
                            </td>
                            <td className="log-target">
                              <span className="target-type">{log.target?.type}</span>
                              <span className="target-label">{log.target?.label}</span>
                            </td>
                            <td className="log-comment">{log.comment || '—'}</td>
                            <td className="log-ip">{log.ipAddress || '—'}</td>
                            <td style={{display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center'}}>
                              {log.changes?.length > 0 && (
                                <button
                                  className="btn-expand"
                                  onClick={() => setExpandedLog(isExpanded ? null : log._id)}
                                >
                                  {isExpanded ? '▲' : '▼'} {log.changes.length} champ{log.changes.length > 1 ? 's' : ''}
                                </button>
                              )}
                              <button
                                className="btn-expand"
                                style={{background: '#38bdf8', color: 'white', border: 'none'}}
                                onClick={() => setSelectedLogForModal(log)}
                                title="Voir la fiche détaillée"
                              >
                                👁️ Détails
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="changes-row">
                              <td colSpan="8">
                                <div className="changes-panel">
                                  <div className="changes-title">Détail des modifications</div>
                                  <table className="changes-table">
                                    <thead>
                                      <tr>
                                        <th>Champ</th>
                                        <th>Ancienne valeur</th>
                                        <th>Nouvelle valeur</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {log.changes.map((c, i) => (
                                        <tr key={i}>
                                          <td><code>{c.field}</code></td>
                                          <td className="old-value">
                                            {c.oldValue === null || c.oldValue === undefined
                                              ? <em>vide</em>
                                              : <span>{JSON.stringify(c.oldValue)}</span>
                                            }
                                          </td>
                                          <td className="new-value">
                                            {c.newValue === null || c.newValue === undefined
                                              ? <em>vide</em>
                                              : <span>{JSON.stringify(c.newValue)}</span>
                                            }
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {log.deletedSnapshot && (
                                    <div className="snapshot-section">
                                      <div className="snapshot-title">📦 Snapshot des données supprimées</div>
                                      <pre className="snapshot-code">
                                        {JSON.stringify(log.deletedSnapshot, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {logs.length === 0 && !loading && (
                      <tr>
                        <td colSpan="8" className="no-data">Aucun log trouvé pour ces critères</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  {Array.from({ length: Math.min(pagination.pages, 10) }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      className={`page-btn ${pagination.page === p ? 'active' : ''}`}
                      onClick={() => fetchLogs(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Tab: Sessions ─────────────────────────────────────────────── */}
        {activeTab === 'sessions' && (
          <div className="sessions-section">
            {loading ? (
              <div className="audit-loading"><div className="spinner" /><span>Chargement...</span></div>
            ) : (
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Rôle</th>
                    <th>Connexion</th>
                    <th>Déconnexion</th>
                    <th>Durée</th>
                    <th>Appareil</th>
                    <th>Navigateur</th>
                    <th>IP</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(s => (
                    <tr key={s._id}>
                      <td>{s.prenom} {s.nom}</td>
                      <td>
                        <span className="role-pill" style={{ background: ROLE_COLORS[s.role] || '#718096' }}>
                          {s.role}
                        </span>
                      </td>
                      <td className="log-datetime">{formatDateTime(s.loginAt)}</td>
                      <td className="log-datetime">{formatDateTime(s.logoutAt)}</td>
                      <td>{formatDuration(s.duration)}</td>
                      <td>{s.deviceType} / {s.os}</td>
                      <td>{s.browser}</td>
                      <td className="log-ip">{s.ipAddress}</td>
                      <td>
                        <span className={`status-pill ${s.status}`}>
                          {s.status === 'active' ? '🟢 En ligne' : s.status === 'closed' ? '⚫ Terminée' : '⏱️ Expirée'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan="9" className="no-data">Aucune session</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ─── Tab: Stats ────────────────────────────────────────────────── */}
        {activeTab === 'stats' && stats && (
          <div className="stats-section">
            <div className="stats-grid">
              <div className="stats-card">
                <h3>🔝 Top Actions (30 jours)</h3>
                <div className="stats-list">
                  {stats.actionBreakdown?.map(item => (
                    <div key={item._id} className="stats-item">
                      <div className="stats-item-left">
                        <span className="action-badge"
                          style={{
                            background: getActionConfig(item._id).bg,
                            color: getActionConfig(item._id).color
                          }}>
                          {getActionConfig(item._id).icon} {getActionConfig(item._id).label}
                        </span>
                      </div>
                      <div className="stats-item-right">
                        <div className="stats-bar" style={{
                          width: `${Math.min(100, (item.count / (stats.actionBreakdown[0]?.count || 1)) * 100)}%`,
                          background: getActionConfig(item._id).color
                        }} />
                        <span className="stats-count">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stats-card">
                <h3>👥 Top Utilisateurs (30 jours)</h3>
                <div className="stats-list">
                  {stats.userBreakdown?.map(item => (
                    <div key={item._id} className="stats-item">
                      <div className="stats-item-left">
                        <span className="role-pill" style={{ background: ROLE_COLORS[item.role] || '#718096' }}>
                          {item.role}
                        </span>
                        <span className="user-name">{item.prenom} {item.nom}</span>
                      </div>
                      <div className="stats-item-right">
                        <div className="stats-bar" style={{
                          width: `${Math.min(100, (item.count / (stats.userBreakdown[0]?.count || 1)) * 100)}%`,
                          background: ROLE_COLORS[item.role] || '#718096'
                        }} />
                        <span className="stats-count">{item.count} actions</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modal Détails Log ────────────────────────────────────────── */}
      {selectedLogForModal && (
        <div className="modal-overlay" onClick={() => setSelectedLogForModal(null)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{background: 'white', borderRadius: '12px', padding: '25px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px'}}>
              <h2 style={{margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{background: getActionConfig(selectedLogForModal.action).bg, color: getActionConfig(selectedLogForModal.action).color, padding: '6px 12px', borderRadius: '20px', fontSize: '0.9rem'}}>
                  {getActionConfig(selectedLogForModal.action).icon} {getActionConfig(selectedLogForModal.action).label}
                </span>
                Détail de l'activité
              </h2>
              <button onClick={() => setSelectedLogForModal(null)} style={{background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer'}}>&times;</button>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
              <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                <h3 style={{fontSize: '0.9rem', color: '#64748b', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Acteur (Utilisateur)</h3>
                <p style={{margin: '0 0 5px 0', fontWeight: 'bold', color: '#0f172a'}}>{selectedLogForModal.actor?.prenom} {selectedLogForModal.actor?.nom}</p>
                <p style={{margin: 0, fontSize: '0.85rem'}}><span className="role-pill" style={{ background: ROLE_COLORS[selectedLogForModal.actor?.role] || '#718096' }}>{selectedLogForModal.actor?.role}</span></p>
                <p style={{margin: '10px 0 0 0', fontSize: '0.85rem', color: '#475569'}}>ID: <code>{selectedLogForModal.actor?.userId}</code></p>
              </div>

              <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                <h3 style={{fontSize: '0.9rem', color: '#64748b', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Contexte technique</h3>
                <p style={{margin: '0 0 5px 0', fontSize: '0.85rem', color: '#0f172a'}}><strong>Date:</strong> {formatDateTime(selectedLogForModal.timestamp)}</p>
                <p style={{margin: '0 0 5px 0', fontSize: '0.85rem', color: '#0f172a'}}><strong>IP:</strong> {selectedLogForModal.ipAddress || '—'}</p>
                <p style={{margin: '0 0 5px 0', fontSize: '0.85rem', color: '#0f172a'}}><strong>Navigateur:</strong> <span style={{wordBreak: 'break-all'}}>{selectedLogForModal.userAgent || '—'}</span></p>
              </div>
            </div>

            <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px'}}>
              <h3 style={{fontSize: '0.9rem', color: '#64748b', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Cible & Commentaire</h3>
              <p style={{margin: '0 0 5px 0', fontSize: '0.9rem'}}><strong>Type cible:</strong> {selectedLogForModal.target?.type}</p>
              <p style={{margin: '0 0 5px 0', fontSize: '0.9rem'}}><strong>Label:</strong> {selectedLogForModal.target?.label}</p>
              <p style={{margin: '0 0 5px 0', fontSize: '0.9rem'}}><strong>ID Cible:</strong> <code>{selectedLogForModal.target?.id}</code></p>
              {selectedLogForModal.target?.parentId && <p style={{margin: '0 0 5px 0', fontSize: '0.9rem'}}><strong>ID Parent:</strong> <code>{selectedLogForModal.target?.parentId}</code></p>}
              <div style={{marginTop: '10px', padding: '10px', background: 'white', borderLeft: '4px solid #3b82f6', borderRadius: '4px'}}>
                <p style={{margin: 0, fontStyle: 'italic', color: '#475569'}}>{selectedLogForModal.comment || 'Aucun commentaire.'}</p>
              </div>
            </div>

            {selectedLogForModal.changes?.length > 0 && (
              <div style={{marginBottom: '20px'}}>
                <h3 style={{fontSize: '0.9rem', color: '#64748b', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Champs modifiés ({selectedLogForModal.changes.length})</h3>
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                    <thead>
                      <tr style={{background: '#f1f5f9'}}>
                        <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #cbd5e1', color: '#475569'}}>Champ</th>
                        <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #cbd5e1', color: '#475569'}}>Ancienne valeur</th>
                        <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #cbd5e1', color: '#475569'}}>Nouvelle valeur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLogForModal.changes.map((c, i) => (
                        <tr key={i}>
                          <td style={{padding: '8px', borderBottom: '1px solid #e2e8f0', fontWeight: '500', color: '#0f172a'}}><code>{c.field}</code></td>
                          <td style={{padding: '8px', borderBottom: '1px solid #e2e8f0', color: '#ef4444', wordBreak: 'break-all'}}>{c.oldValue === null || c.oldValue === undefined ? <em>vide</em> : JSON.stringify(c.oldValue)}</td>
                          <td style={{padding: '8px', borderBottom: '1px solid #e2e8f0', color: '#10b981', wordBreak: 'break-all'}}>{c.newValue === null || c.newValue === undefined ? <em>vide</em> : JSON.stringify(c.newValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedLogForModal.deletedSnapshot && (
              <div>
                <h3 style={{fontSize: '0.9rem', color: '#64748b', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Données supprimées (Snapshot)</h3>
                <pre style={{background: '#1e293b', color: '#e2e8f0', padding: '15px', borderRadius: '8px', overflowX: 'auto', fontSize: '0.8rem', margin: 0}}>
                  {JSON.stringify(selectedLogForModal.deletedSnapshot, null, 2)}
                </pre>
              </div>
            )}

            <div style={{marginTop: '20px', textAlign: 'right'}}>
              <button onClick={() => setSelectedLogForModal(null)} style={{padding: '8px 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500'}}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
